import { Config } from "@atlaskit/react-ufo/config";
import type { InteractionMetrics } from "@atlaskit/react-ufo/interaction-metrics";
import { init as initReactUFO } from "@atlaskit/react-ufo/interaction-metrics-init";
import { configure as configureResourceTimings } from "@atlaskit/react-ufo/resource-timing";
import {
    type SSRConfig,
    configure as configureSSR
} from "@atlaskit/react-ufo/ssr";
import { ReportedTimings } from "@atlaskit/react-ufo/ssr";
import traceUFOPageLoad from "@atlaskit/react-ufo/trace-pageload";
import type AnalyticsWebClient from "@atlassiansox/analytics-web-client";

import { assetsClassification } from "./utils/get-asset-classification";
import { getCustomData } from "./utils/get-custom-data";
import { resourceTimingsConfig } from "./utils/get-resource-config";

declare global {
    interface Window {
        SSR_detailMetrics?: ReportedTimings;
        SSR_totalMetrics?: Record<string, number>;
        SSR_region?: string;
    }
}

export const getUFOExperienceKey = (path: string): string => {
    if (path.length) {
        if (window !== undefined && window.isUniform) {
            if (window.compositionStatusCode !== 404) {
                return "uniform.all";
            }
            return "uniform.404";
        }

        return window !== undefined && window.cmsPageVariant
            ? path.replace(/^\//, "").replace(/\//g, "-").toLowerCase() +
                  "-" +
                  window.cmsPageVariant
            : path.replace(/^\//, "").replace(/\//g, "-").toLowerCase();
    }
    return "home";
};

export const getSSRTotal = (): number => {
    try {
        return (window.SSR_totalMetrics || {})["duration"] || 0;
    } catch (e) {
        console.warn("Error getting SSR total", e);
        return 0;
    }
};

export const getSSRTimings = (): ReportedTimings => {
    try {
        return window.SSR_detailMetrics || {};
    } catch (e) {
        console.warn("Error getting SSR timings", e);
        return {};
    }
};

export const getAdditionalFields = (interaction: InteractionMetrics) => {
    try {
        return { custom: getCustomData(interaction) };
    } catch (error) {
        return {};
    }
};

const getFCPStartTime = (): number => {
    try {
        const paintEntries = window.performance.getEntriesByType("paint");
        const firstContentfulPaint = paintEntries.find(
            (entry) => entry.name === "first-contentful-paint"
        );
        return firstContentfulPaint?.startTime || 0;
    } catch (e) {
        console.warn("Error getting first contentful paint", e);
        return 0;
    }
};

// SSR Configuration
export const ssrConfig: SSRConfig = {
    getDoneMark: getSSRTotal,
    getTimings: getSSRTimings,
    getFeatureFlags: () => null
};

// UFO Configuration
export const config: Config = {
    ssr: { getSSRDoneTime: getFCPStartTime },
    product: "marketing",
    region: window.SSR_region || "unknown",
    additionalPayloadData: getAdditionalFields,
    assetsClassification: assetsClassification,
    kind: {
        page_load: 1,
        transition: 1,
        hover: 0,
        legacy: 1,
        typing: 0,
        press: 1
    },
    capability: {
        feature_flag_access: 100,
        react_profiler: 1
    },
    segmentPrefix: "ufo-",
    typingMethod: "timeout",
    removeInteractionsUFOPrefixes: true,
    removePageSegmentsUFOPrefixes: false,
    vc: {
        enabled: true,
        heatmapSize: 200,
        oldDomUpdates: false,
        devToolsEnabled: true,
        selectorConfig: {
            id: false,
            role: true,
            className: true,
            testId: true
        }
    }
};

const handleDynamicImportFailures = () => {
    if (typeof window !== "undefined") {
        window.addEventListener("unhandledrejection", (event) => {
            const reason =
                event.reason ??
                (event as unknown as CustomEvent).detail?.reason;
            if (reason) {
                const message =
                    typeof reason === "string" ? reason : reason.message;
                if (
                    typeof message === "string" &&
                    message.includes("dynamically imported module") &&
                    (message.includes("segment-highlight") ||
                        message.includes("create-payload") ||
                        message.includes("create-post-interaction-log-payload"))
                ) {
                    console.error(reason);
                    event.preventDefault();
                    // Prevent the error from reaching the global handler.
                    // This keeps the error out of our Sentry logs.
                    event.stopImmediatePropagation();
                    return;
                }
            }
        });
    }
};

export const initUFO = (analyticsClient: AnalyticsWebClient, path: string) => {
    // HACK: react-ufo does some unprotected dynamic importing internally.
    // So, we attach a global handler for when those dynamic imports fail
    // (usually due to network problems), report them in the console,
    // and move on.
    handleDynamicImportFailures();

    // Resource Timings Configuration
    configureResourceTimings(resourceTimingsConfig);
    // SSR Configuration
    configureSSR(ssrConfig);
    // UFO Configuration
    initReactUFO(Promise.resolve(analyticsClient), config);

    const ufoName = getUFOExperienceKey(path);
    traceUFOPageLoad(ufoName, path || "/");
};
